import { NotebookIcon } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';

import { FormCreatableSelect, FormField, FormInput } from '@/_components';
import { CardContainer, FormFilesInput, FormInputEmail, FormInputUrl, FormLayout, SectionHeading } from '@/_containers';
import { RichText } from '@/_containers/inputs/rich-text';
import type { ContactQuerySchema } from '@/_pages/contacts/shared';
import { RecruitingHiringCriteriaCategoriesMultiSelect } from '@/_pages/settings/recruiting-hiring-criteria-categories/shared/components';
import { RecruitingJobCategoriesMultiSelect } from '@/_pages/settings/recruiting-job-categories/shared/components';
import { RecruitingStatusesSelect } from '@/_pages/settings/recruiting-statuses/shared/components';
import { showFormField } from '@/_shared';

import { ContactSelect } from './contact-select';
import { ReferralCombobox } from './referral-combobox';

import type { RecruitingFormSchema } from '../schemas';

export function FormFields({
  form,
  isEditable = true,
  isCreate = false,
  autoFocus = false,
}: {
  form: UseFormReturn<RecruitingFormSchema>;
  isEditable?: boolean;
  isCreate?: boolean;
  autoFocus?: boolean;
}) {
  const [id, resume, additional_documents, notes, recruiting_tags] = form.watch([
    'id',
    'resume',
    'additional_documents',
    'notes',
    'recruiting_tags',
  ]);
  const folder_path = `recruiting/${id}`;

  const handleContactChange = (value: ContactQuerySchema | null) => {
    if (!value) {
      form.setValue('first_name', '');
      form.setValue('last_name', '');
      form.setValue('email', '');
      form.setValue('linked_in', '');
      form.setValue('phone_numbers', []);
      form.setValue('profile_image', []);
      return;
    }

    form.setValue('first_name', value.first_name, { shouldDirty: true });
    form.setValue('last_name', value.last_name || '', { shouldDirty: true });
    form.setValue('email', value.email || '', { shouldDirty: true });
    form.setValue('linked_in', value.linked_in_url || '', { shouldDirty: true });
    form.setValue('phone_numbers', value.phone_numbers || [], { shouldDirty: true });
    form.setValue('profile_image', value.profile_image || [], { shouldDirty: true });
  };

  return (
    <>
      <CardContainer>
        <SectionHeading text="Details" icon="/images/icons/personaldetails.svg" />
        <FormLayout>
          {isCreate && <ContactSelect isEditable={isEditable} onChange={handleContactChange} autoFocus={autoFocus} />}

          <FormField
            control={form.control}
            name="first_name"
            render={({ field }) => (
              <FormInput
                formItemClassName="col-span-6"
                label="First Name (required)"
                {...field}
                disabled={!isEditable}
                autoFocus={!isCreate && autoFocus}
              />
            )}
          />

          <FormField
            control={form.control}
            name="last_name"
            render={({ field }) => (
              <FormInput
                formItemClassName="col-span-6"
                label="Last Name (required)"
                {...field}
                disabled={!isEditable}
              />
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormInputEmail
                formItemClassName="col-span-6"
                label="Email"
                type="email"
                {...field}
                disabled={!isEditable}
              />
            )}
          />

          <FormField
            control={form.control}
            name="linked_in"
            render={({ field }) => (
              <FormInputUrl formItemClassName="col-span-6" label="LinkedIn" {...field} disabled={!isEditable} />
            )}
          />

          <FormField
            control={form.control}
            name="recruiting_status"
            render={({ field: { value, onChange } }) => (
              <RecruitingStatusesSelect
                formItemClassName="col-span-6"
                label="Recruiting Status"
                value={value}
                disabled={!isEditable}
                onValueChange={(value) => {
                  form.setValue('recruiting_status_id', value?.id, { shouldDirty: true });
                  onChange(value);
                }}
              />
            )}
          />

          <FormField
            control={form.control}
            name="recruiting_job_categories"
            render={({ field: { ref: _, ...field } }) => (
              <RecruitingJobCategoriesMultiSelect
                formItemClassName="col-span-6"
                label="Job Categories"
                {...field}
                disabled={!isEditable}
                onValueChange={(value) => {
                  field.onChange(value);
                }}
              />
            )}
          />

          <FormField
            control={form.control}
            name="hiring_criteria_categories"
            render={({ field: { ref: _, ...field } }) => (
              <RecruitingHiringCriteriaCategoriesMultiSelect
                formItemClassName="col-span-12 md:col-span-6 2xl:col-span-6"
                label="Hiring Criteria Categories"
                {...field}
                disabled={!isEditable}
                onValueChange={(value) => {
                  field.onChange(value);
                }}
              />
            )}
          />

          <FormField
            control={form.control}
            name="referral"
            render={({ field: { ref: _, ...rest } }) => (
              <ReferralCombobox {...rest} disabled={!isEditable} className="col-span-12 md:col-span-6 2xl:col-span-6" />
            )}
          />

          {showFormField(isEditable, recruiting_tags) && (
            <FormField
              control={form.control}
              name="recruiting_tags"
              render={({ field }) => (
                <FormCreatableSelect
                  label="Tags"
                  isMulti
                  isClearable
                  value={field.value}
                  onChange={field.onChange}
                  formItemClassName="col-span-12"
                  getOptionLabel={(item) => item.name}
                  getNewOptionData={(inputValue: string) => ({ name: inputValue, value: inputValue })}
                  disabled={!isEditable}
                />
              )}
            />
          )}
        </FormLayout>
      </CardContainer>

      {showFormField(isEditable, resume || additional_documents) && (
        <CardContainer>
          <SectionHeading text="Documents" icon={NotebookIcon} />

          <FormLayout>
            {showFormField(isEditable, resume) && (
              <FormField
                control={form.control}
                name="resume"
                render={({ field }) => (
                  <FormFilesInput
                    label="Resume"
                    formItemClassName="col-span-12"
                    folder_path={folder_path}
                    {...field}
                    disabled={!isEditable}
                    maxFiles={1}
                  />
                )}
              />
            )}

            {showFormField(isEditable, additional_documents) && (
              <FormField
                control={form.control}
                name="additional_documents"
                render={({ field }) => (
                  <FormFilesInput
                    label="Additional Documents"
                    formItemClassName="col-span-12 mt-4"
                    folder_path={folder_path}
                    {...field}
                    disabled={!isEditable}
                  />
                )}
              />
            )}
          </FormLayout>
        </CardContainer>
      )}
      {showFormField(isEditable, notes) && (
        <CardContainer>
          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <RichText
                label="Notes"
                value={field.value || ''}
                onChange={field.onChange}
                disabled={!isEditable}
                inline={!isEditable}
                mediaFolderPath={folder_path}
                className="col-span-12"
              />
            )}
          />
        </CardContainer>
      )}
    </>
  );
}
